import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiFetch, ApiError } from '@/lib/api';
import { useWorkspaceId, usePermissions, useWorkspace, useFeature } from '@/contexts/WorkspaceContext';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { formatTemplateName } from '@/lib/templateName';
import { formatCredits } from '@/lib/creditCosts';
import { extractTemplateVariables, needsMappingReview, type MappingReview } from '@/lib/templateVariables';
import type { SendRequirements } from '@/lib/whatsappPreview';
import TemplateComposerDialog from '@/components/TemplateComposerDialog';
import MediaAssetGrid from '@/components/MediaAssetGrid';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { withOptOutButton, CONTACT_FIELDS, ASSET_KIND_FOR_HEADER } from '@/lib/templateComposer';
import type { TemplateDraft, TemplateMediaAsset, draftToPayload } from '@/lib/templateComposer';
import { draftFromMedia, mediaHeaderAssets, type MediaForTemplate } from '@/lib/templateFromMedia';
import { campaignFromTemplatePath } from '@/lib/flowLinks';
import { normalize } from '@/lib/generatedContent';
import { toast } from 'sonner';
import {
  MoreHorizontal,
  RefreshCw,
  MessageSquare,
  Eye,
  Loader2,
  Send,
  Phone,
  Pencil,
  AlertTriangle,
  Plus,
  Trash2,
  Globe,
  Megaphone,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Templates are authored HERE and mirrored from the provider.
//
// CEQUENS has no creation API — their support confirmed it, and that is still true — so creating,
// editing and deleting a template goes direct to Meta's Graph API against the WhatsApp Business
// Account. The list, every send, and delivery tracking are all still CEQUENS'. What has NOT changed:
// the provider console is deliberately never linked or named for users (it is our internal
// dashboard, not theirs), and `variableMapping` — the link from {{1}}, {{2}} … to a contact field —
// is still the one thing no provider has a concept of.
//
// Not every template here is editable. A workspace may only change the ones it created; templates we
// author for everyone, and those from the provider console, are read-only to it. The server enforces
// that (templateWriteRefusal); the menu below simply does not offer what would be refused.

interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejectionReason: string | null;
  headerType: string;
  headerContent: string | null;
  body: string;
  footer: string | null;
  buttons: Array<{ type: string; text: string; url?: string; phoneNumber?: string }>;
  variableMapping: Record<string, string>;
  // 'public' means this template is shared with every workspace. Only an admin creates one, and
  // only an admin may change it.
  visibility?: 'workspace' | 'public';
  remoteName?: string | null;
  // `variableLabels` is what the provider called each variable, captured at sync so this page can
  // show `{{2}} — "Developer"` rather than a bare number. Derived by the backend; display only.
  //
  // `mappingReview` is not display-only: sync raises it when the provider rewrote this template's
  // body underneath a saved mapping, and a launch is blocked until a human confirms the mapping
  // still means what it said. Server-owned — the API no longer accepts `metadata` on update.
  metadata?: {
    variableLabels?: Record<string, string>;
    mappingReview?: MappingReview | null;
    // Set when WE created it at Meta, which is also what makes it editable here.
    createdVia?: string;
  } | null;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  // The generated media this template was made from, and the photo or video it was reviewed with:
  // what a campaign using it starts with. Ours alone, so it changes without a re-review.
  campaignMediaId?: string | null;
  headerAssetUid?: string | null;
}

// The single-template answer's description of that default (GET /:id). Null when there is none.
interface DefaultHeaderMedia {
  campaignMediaId: string;
  mediaName: string;
  assetUid: string | null;
  kind: 'photo' | 'video' | null;
  mime: string | null;
  previewUrl: string | null;
}

// The diagnostic the test-send endpoint answers with. A provider refusal comes back as a SUCCESS
// with `accepted: false` — the request worked, the send did not — so the reason and the exact
// payload survive to be shown here instead of being flattened into a toast.
//
// `accepted` is NOT `delivered`, and was called that until it caused real confusion: it means the
// provider took the request and returned a message id. The message can still die inside Meta
// minutes later. Actual delivery arrives on the status webhook and is polled separately below.
interface TestSendResult {
  accepted: boolean;
  templateName?: string;
  remoteName?: string;
  providerMessageId?: string | null;
  sentComponents?: unknown;
  // 'id' means the media was uploaded and WhatsApp fetches nothing; 'link' means we fell back.
  mediaMode?: 'id' | 'link';
  // Whether the number resolved to a WhatsApp account. `null`/undefined = the provider said
  // nothing either way, which is not the same as a failure.
  recipientResolved?: boolean | null;
  warning?: string;
  error?: string;
  providerError?: { code: number | null; title: string | null; details: string | null } | null;
  providerStatus?: number | null;
  requestId?: string | null;
  // A test send is a real message and is charged like one; refused sends are refunded (0).
  creditsCharged?: number;
  creditsRemaining?: number;
}

// The real outcome, read back from the status webhook. `accepted` means nothing has been heard
// yet — which is also what it looks like when the webhook is not reaching this deployment — so it
// is never rendered as a failure.
interface TestSendDelivery {
  providerMessageId: string;
  status: 'accepted' | 'sent' | 'delivered' | 'read' | 'failed';
  deliveredAt: string | null;
  readAt: string | null;
  errorMessage: string | null;
  failureCode: string | null;
}

const DELIVERY_LABELS: Record<TestSendDelivery['status'], string> = {
  accepted: 'Waiting for WhatsApp to report back…',
  sent: 'Sent — WhatsApp has it',
  delivered: 'Delivered to the handset',
  read: 'Read by the recipient',
  failed: 'Failed',
};

// What the list endpoint reports about this workspace's authoring ability. `templatesLimit: null`
// means unlimited, which is also what a platform admin sees.
interface Capabilities {
  canCreate: boolean;
  canUploadSamples: boolean;
  templatesUsed: number;
  templatesLimit: number | null;
}

const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-500/20 text-yellow-700 border-yellow-300',
  APPROVED: 'bg-green-500/20 text-green-700 border-green-300',
  REJECTED: 'bg-red-500/20 text-red-700 border-red-300',
  PAUSED: 'bg-gray-500/20 text-gray-700 border-gray-300',
  DISABLED: 'bg-gray-500/20 text-gray-700 border-gray-300',
};

export default function WhatsAppTemplates() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // A sync failure the operator should keep seeing. `forbidden` in particular is a standing
  // account-level condition — WhatsApp API access not enabled on the Cequens side — so it belongs
  // on the page rather than in a toast that vanishes and reappears on every visit.
  const [syncIssue, setSyncIssue] = useState<{ kind: string; message: string } | null>(null);

  // How many templates are standing unconfirmed after a provider-side body edit. Drives the banner
  // below; the per-row badge reads each template's own flag.
  const [reviewCount, setReviewCount] = useState(0);

  // Variable-mapping editor. An approved template's NAME is frozen — a rename voids the Meta
  // approval — so the mapping is the only thing still ours to fix after the fact. Without this, a
  // wrong mapping meant deleting the record and starting over.
  //
  // The body is NOT frozen, though this comment used to say it was: Meta allows rate-limited edits
  // to an approved template, and sync mirrors them. That is why saving here doubles as confirming
  // the mapping still fits a body the provider has since changed.
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [mappingDraft, setMappingDraft] = useState<Record<string, string>>({});

  // Test send state
  const [isTestSendOpen, setIsTestSendOpen] = useState(false);
  const [testSendTemplate, setTestSendTemplate] = useState<WhatsAppTemplate | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testChannel, setTestChannel] = useState<'sms' | 'whatsapp'>('whatsapp');
  const [testMessage, setTestMessage] = useState('');
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [testSending, setTestSending] = useState(false);

  // Header and button values live apart from `testVariables` because neither is part of
  // `variableMapping` — that maps body indices to contact fields and nothing else.
  const [requirements, setRequirements] = useState<SendRequirements | null>(null);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [testHeaderText, setTestHeaderText] = useState('');
  const [testHeaderMediaUrl, setTestHeaderMediaUrl] = useState('');
  // An id from the media endpoint. When set it wins over the URL: the file is already with the
  // provider, so WhatsApp never has to fetch anything — which is the failure this whole flow had.
  const [testHeaderMediaId, setTestHeaderMediaId] = useState('');
  const [uploadedMediaName, setUploadedMediaName] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [testButtonValues, setTestButtonValues] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<TestSendResult | null>(null);
  const [delivery, setDelivery] = useState<TestSendDelivery | null>(null);

  // What the server says this workspace may do, plus the quota. Both come from the list response:
  // the client holds the LIMIT via entitlements but never the USED count, which is a prefix-scoped
  // query only the server can run.
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<'create' | 'edit'>('create');
  const [composerInitial, setComposerInitial] = useState<Partial<TemplateDraft> | null>(null);
  const [composerTemplateId, setComposerTemplateId] = useState<string | null>(null);
  const [composerSubmitting, setComposerSubmitting] = useState(false);
  // The media a template is being made from (or an edited one was made from): its photos and videos
  // are the header picker, and its id is what the sample upload reads them from.
  const [composerMedia, setComposerMedia] = useState<{ id: string; name: string } | null>(null);
  const [composerAssets, setComposerAssets] = useState<TemplateMediaAsset[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  // The detail dialog's "Default campaign media", and the picker for changing it.
  // undefined while loading; null when there is none (or its media has gone).
  const [detailMedia, setDetailMedia] = useState<DefaultHeaderMedia | null | undefined>(undefined);
  const [detailAssets, setDetailAssets] = useState<TemplateMediaAsset[] | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);

  const workspaceId = useWorkspaceId();
  const { can } = usePermissions();
  const { isSuperAdmin, refreshWorkspace } = useWorkspace();
  const showError = useApiErrorToast();
  const { guard, nudge } = useSubscriptionGuard('Managing WhatsApp templates requires an active subscription.');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasCustomTemplates = useFeature('templates_custom');

  const canManage = can('templates:manage');
  // "Create campaign" from an approved template — the hand-off to the campaign wizard.
  const canCreateCampaign = can('campaign:create');
  const atQuota = Boolean(
    capabilities
    && capabilities.templatesLimit !== null
    && capabilities.templatesUsed >= capabilities.templatesLimit,
  );

  /**
   * May this workspace delete this template?
   *
   * Mirrors templateWriteRefusal on the server: ownership is the remote name carrying this
   * workspace's prefix. The sync drops every other workspace's prefix, so any prefixed row here is
   * this workspace's own. A super-admin may touch anything. The server enforces it regardless —
   * this only decides whether to offer an action that would be refused.
   */
  const canDeleteTemplate = (template: WhatsAppTemplate) => {
    if (isSuperAdmin) return true;
    return Boolean(template.remoteName) && template.visibility !== 'public';
  };

  /**
   * May this workspace edit this template? Narrower than delete: the composer is pre-filled from
   * what we stored when we created the template, so only one created here can be edited here.
   */
  const canEditTemplate = (template: WhatsAppTemplate) => {
    if (isSuperAdmin) return true;
    return canDeleteTemplate(template) && template.metadata?.createdVia === 'meta';
  };

  // `silent` skips the loading spinner. A refetch after a sync should update the rows in place
  // rather than blanking the table the user is already reading.
  const fetchTemplates = async ({ silent }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      params.append('limit', '50');

      const data = await apiFetch<{
        templates: WhatsAppTemplate[];
        capabilities?: Capabilities;
        pagination: { total: number };
      }>(`/workspaces/${workspaceId}/whatsapp-templates?${params.toString()}`);

      setTemplates(data.templates);
      setTotal(data.pagination.total);
      if (data.capabilities) setCapabilities(data.capabilities);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Reading the local mirror. Deliberately separate from the sync effect below: changing a
  // filter must never trigger a call to CEQUENS.
  useEffect(() => {
    fetchTemplates();
  }, [workspaceId, statusFilter, categoryFilter]);

  // --- authoring ---------------------------------------------------------------------------

  /** A media item's sendable photos and videos, as the composer's header picker offers them. */
  const assetsOf = (media: MediaForTemplate) => {
    const gc = normalize(media.generatedContent);
    return [...mediaHeaderAssets(gc, 'photo'), ...mediaHeaderAssets(gc, 'video')];
  };

  const openComposer = async (template?: WhatsAppTemplate) => {
    setComposerMedia(null);
    setComposerAssets([]);
    if (template) {
      // A template made from generated media is edited against that media's assets, so its header
      // picture is already there — the sample is re-sent from it instead of being asked for again.
      // If the media cannot be read, the edit simply works as it always did: upload a sample.
      let headerPreviewUrl: string | null = null;
      if (template.campaignMediaId) {
        try {
          const { media } = await apiFetch<{ media: MediaForTemplate }>(
            `/workspaces/${workspaceId}/campaign-media/${template.campaignMediaId}`,
          );
          const assets = assetsOf(media);
          setComposerMedia({ id: media.id, name: media.name });
          setComposerAssets(assets);
          headerPreviewUrl = assets.find((asset) => asset.uid === template.headerAssetUid)?.url ?? null;
        } catch (err) {
          console.warn('[Templates] could not load the media this template was made from:', err);
        }
      }
      setComposerMode('edit');
      setComposerTemplateId(template.id);
      setComposerInitial({
        campaignMediaId: template.campaignMediaId ?? null,
        headerAssetUid: template.headerAssetUid ?? null,
        headerPreviewUrl,
        name: template.name,
        language: template.language,
        category: (template.category === 'UTILITY' ? 'UTILITY' : 'MARKETING'),
        headerType: (template.headerType as TemplateDraft['headerType']) || 'NONE',
        headerContent: template.headerContent || '',
        body: template.body,
        // The provider's stored examples double as the samples, so an edit starts from what the
        // template was actually approved with rather than from blank fields.
        bodySamples: template.metadata?.variableLabels || {},
        variableMapping: template.variableMapping || {},
        footer: template.footer || '',
        // A template made before the opt-out button was required gains one here, since the server
        // refuses to save it without.
        buttons: withOptOutButton((template.buttons || []).map((b) => ({
          type: (b.type as 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER') || 'QUICK_REPLY',
          text: b.text,
          url: b.url,
          phoneNumber: b.phoneNumber,
        })), template.language),
      });
    } else {
      setComposerMode('create');
      setComposerTemplateId(null);
      setComposerInitial(null);
    }
    setComposerOpen(true);
  };

  // Meta's sample upload, which is NOT the media upload used for a test send: this returns an
  // opaque handle used to AUTHOR the template, that returns a media id used to SEND a message.
  const uploadSample = async (file: File, headerFormat: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('headerFormat', headerFormat);
    try {
      const result = await apiFetch<{ handle: string; filename: string | null }>(
        `/workspaces/${workspaceId}/whatsapp-templates/media-sample`,
        { method: 'POST', body: form },
      );
      return { handle: result.handle, filename: result.filename || file.name };
    } catch (err) {
      showError(err, 'Could not upload the sample');
      return null;
    }
  };

  // The same handle, from one of the media's own generated photos or videos: the server reads the
  // object and hands it to Meta, so nobody downloads their picture just to upload it back. Rejects
  // with the server's reason, which the composer shows beside the picture it is about.
  const prepareAssetSample = async (assetUid: string, headerFormat: string) => {
    if (!composerMedia) throw new Error('This template is not linked to a media item.');
    const result = await apiFetch<{ handle: string; filename: string }>(
      `/workspaces/${workspaceId}/whatsapp-templates/media-sample/from-asset`,
      {
        method: 'POST',
        body: JSON.stringify({ campaignMediaId: composerMedia.id, assetUid, headerFormat }),
      },
    );
    return { handle: result.handle, filename: result.filename };
  };

  // "Use in a template" on a media item lands here as ?fromMedia=<id>, and opens the composer
  // pre-filled from it. Waits for `capabilities` (they arrive with the list), so it can refuse up
  // front — without permission, on a server that cannot create, at the plan's quota, or on a plan
  // without custom templates — rather than letting somebody fill in a form bound to be refused.
  //
  // A ref rather than a cancel flag: StrictMode runs this twice, and a cancelled first run plus an
  // already-stripped parameter would leave nothing to open. The parameter goes only once the
  // composer is open, so a reload before then still lands somewhere sensible.
  const fromMedia = searchParams.get('fromMedia');
  const openedFromMedia = useRef<string | null>(null);
  useEffect(() => {
    if (!fromMedia || !capabilities || openedFromMedia.current === fromMedia) return;
    openedFromMedia.current = fromMedia;

    const dropParam = () => setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.delete('fromMedia');
      return next;
    }, { replace: true });

    const refusal = !canManage
      ? 'Ask a workspace owner or admin to create templates — your role cannot.'
      : !capabilities.canCreate
        ? 'Creating templates is not available on this server.'
        : atQuota
          ? `Your plan allows ${capabilities.templatesLimit} templates, and all of them are in use.`
          : null;
    if (refusal) {
      toast.error(refusal);
      dropParam();
      return;
    }
    if (!hasCustomTemplates) {
      // The same upsell the server's 403 produces, without the round trip.
      showError(new ApiError('Custom templates are not in your plan.', 403, { code: 'feature_not_in_plan' }), 'Could not create a template');
      dropParam();
      return;
    }

    (async () => {
      try {
        const { media } = await apiFetch<{ media: MediaForTemplate }>(
          `/workspaces/${workspaceId}/campaign-media/${fromMedia}`,
        );
        const { draft, assets } = draftFromMedia(media, {
          canUploadSamples: capabilities.canUploadSamples,
          takenNames: templates.map((t) => t.name),
        });
        setComposerMode('create');
        setComposerTemplateId(null);
        setComposerMedia({ id: media.id, name: media.name });
        setComposerAssets(assets);
        setComposerInitial(draft);
        setComposerOpen(true);
      } catch (err) {
        showError(err, 'Could not open that media');
      } finally {
        dropParam();
      }
    })();
    // Keyed on the parameter and on capabilities arriving; everything else is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMedia, capabilities]);

  const submitComposer = async (payload: ReturnType<typeof draftToPayload>) => {
    setComposerSubmitting(true);
    try {
      if (composerMode === 'edit' && composerTemplateId) {
        // Name and language are fixed once created, and the edit schema refuses them outright.
        const { name: _name, language: _language, ...editable } = payload;
        await apiFetch(`/workspaces/${workspaceId}/whatsapp-templates/${composerTemplateId}`, {
          method: 'PUT',
          body: JSON.stringify(editable),
        });
        toast.success('Template updated and resubmitted for review');
      } else {
        await apiFetch(`/workspaces/${workspaceId}/whatsapp-templates`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        // The next step, said now: a campaign can only use the template once WhatsApp approves it.
        toast.success('Submitted for review', {
          description: 'WhatsApp usually reviews within a few minutes to a few hours. Once it is approved, '
            + 'use “Create campaign” from its menu.',
        });
      }
      setComposerOpen(false);
      // Refetch rather than sync: the row already exists locally, and /sync-all would spend a
      // provider call to learn what we just wrote.
      await fetchTemplates({ silent: true });
    } catch (err) {
      showError(err, 'Could not save the template');
    } finally {
      setComposerSubmitting(false);
    }
  };

  // The default campaign media is described by the single-template answer — its preview needs a
  // signed URL, which the list deliberately does not mint for every row.
  const loadDetailMedia = async (templateId: string) => {
    try {
      const detail = await apiFetch<WhatsAppTemplate & { defaultHeaderMedia: DefaultHeaderMedia | null }>(
        `/workspaces/${workspaceId}/whatsapp-templates/${templateId}`,
      );
      setDetailMedia(detail.defaultHeaderMedia);
      return detail;
    } catch (err) {
      console.warn('[Templates] could not load the template\'s default media:', err);
      setDetailMedia(null);
      return null;
    }
  };

  const openDetails = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setDetailAssets(null);
    setDetailMedia(template.campaignMediaId ? undefined : null);
    setIsDetailOpen(true);
    if (template.campaignMediaId) loadDetailMedia(template.id);
  };

  // The pictures it could be changed to: the same media's photos (or videos) for this header.
  const loadDetailAssets = async () => {
    if (!selectedTemplate?.campaignMediaId) return;
    try {
      const { media } = await apiFetch<{ media: MediaForTemplate }>(
        `/workspaces/${workspaceId}/campaign-media/${selectedTemplate.campaignMediaId}`,
      );
      const kind = ASSET_KIND_FOR_HEADER[selectedTemplate.headerType];
      setDetailAssets(assetsOf(media).filter((asset) => asset.kind === kind));
    } catch (err) {
      showError(err, 'Could not load that media');
    }
  };

  // Which picture campaigns start with is ours alone: a local PATCH, never a resubmission, so an
  // approved template stays approved and spends none of WhatsApp's edit allowance.
  const saveDefaultMedia = async (changes: { campaignMediaId?: null; headerAssetUid?: string }) => {
    if (!selectedTemplate) return;
    setSavingDefault(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/whatsapp-templates/${selectedTemplate.id}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
      });
      const detail = await loadDetailMedia(selectedTemplate.id);
      if (detail) {
        setSelectedTemplate((prev) => (prev ? {
          ...prev, campaignMediaId: detail.campaignMediaId, headerAssetUid: detail.headerAssetUid,
        } : prev));
      }
      setDetailAssets(null);
      toast.success(changes.campaignMediaId === null ? 'Default media removed' : 'Default media updated');
      await fetchTemplates({ silent: true });
    } catch (err) {
      showError(err, 'Could not change the default media');
    } finally {
      setSavingDefault(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/whatsapp-templates/${deleteTarget.id}`, {
        method: 'DELETE',
      });
      toast.success('Template deleted');
      setDeleteTarget(null);
      await fetchTemplates({ silent: true });
    } catch (err) {
      showError(err, 'Could not delete the template');
    } finally {
      setDeleting(false);
    }
  };

  // The page's one write action: pull the current state of the workspace's templates from
  // CEQUENS. It replaces what used to be three separate buttons — "Import from Cequens"
  // (added new ones), "Sync Status" (updated existing ones) and per-row sync — none of which
  // was a complete picture on its own. The endpoint now adds, updates and prunes in one pass.
  const syncTemplates = async ({ silent }: { silent?: boolean } = {}) => {
    setSyncing(true);
    try {
      const result = await apiFetch<{
        added: number; updated: number; removed: number; needsReview: number; total: number;
        error?: string; errorKind?: string;
      }>(`/workspaces/${workspaceId}/whatsapp-templates/sync-all`, { method: 'POST' });

      if (result.error) {
        setSyncIssue({ kind: result.errorKind || 'unknown', message: result.error });
        // A blocked account is a standing condition, not an event, so the banner carries it and
        // a toast on every page open would just be noise. Anything transient still toasts, and a
        // sync the user asked for always answers.
        if (!silent || result.errorKind !== 'forbidden') {
          toast.warning(result.error);
        }
      } else {
        // Cleared on any success, silent or not — otherwise a banner from an earlier failure
        // would outlive the problem it described.
        setSyncIssue(null);
        // Set on every success including the silent page-load sync, because this is a standing
        // condition rather than an event: a template flagged last week blocks a launch exactly as
        // hard as one flagged a moment ago, and the operator has to see it without pressing sync.
        setReviewCount(result.needsReview || 0);

        if (!silent) {
          const changes = [
            result.added && `${result.added} added`,
            result.updated && `${result.updated} updated`,
            result.removed && `${result.removed} removed`,
          ].filter(Boolean);
          // `updated` counts any changed field now. It used to count status transitions alone, so
          // a sync that rewrote a body reported nothing and this line said "Already up to date" —
          // telling the operator the exact opposite of what had just happened.
          toast.success(
            changes.length
              ? `Synced — ${changes.join(', ')}`
              : `Already up to date (${result.total} template${result.total === 1 ? '' : 's'})`
          );
        }
      }
      await fetchTemplates({ silent: true });
    } catch (err) {
      // Silent on the automatic sync: this path also catches "no active subscription" and
      // permission errors, which would otherwise toast on every page open. The button gives
      // the user a way to see the real reason on demand.
      if (silent) {
        console.warn('[templates] background sync failed', err);
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to sync templates');
      }
    } finally {
      setSyncing(false);
    }
  };

  // Sync on open, once per workspace. The table paints from the local mirror first and this
  // refreshes it behind the scenes, so the page never waits on a provider round-trip. The ref
  // guard keeps React StrictMode's double-mount in development from calling CEQUENS twice.
  const syncedWorkspace = useRef<string | null>(null);
  useEffect(() => {
    if (!workspaceId || syncedWorkspace.current === workspaceId) return;
    syncedWorkspace.current = workspaceId;
    syncTemplates({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const openMappingEditor = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setMappingDraft({ ...(template.variableMapping || {}) });
    setIsMappingOpen(true);
  };

  const handleSaveMapping = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/whatsapp-templates/${selectedTemplate.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ variableMapping: mappingDraft }),
      });
      // Saving a mapping clears that template's review flag server-side, so the banner has to come
      // down with it — otherwise it would keep claiming work that is already done until the next
      // sync. Exactly one flag can clear per save, so a decrement is precise rather than a guess.
      if (needsMappingReview(selectedTemplate)) {
        setReviewCount((n) => Math.max(0, n - 1));
        toast.success('Variable mapping confirmed');
      } else {
        toast.success('Variable mapping updated');
      }
      setIsMappingOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update variable mapping');
    } finally {
      setSubmitting(false);
    }
  };

  const openTestSend = async (template?: WhatsAppTemplate) => {
    setTestSendTemplate(template || null);
    setTestPhone('');
    setTestMessage(template ? '' : 'This is a test message from Targetna AI.');
    setTestChannel(template ? 'whatsapp' : 'sms');
    setTestResult(null);
    setDelivery(null);
    setRequirements(null);
    setTestHeaderText('');
    setTestHeaderMediaUrl('');
    setTestHeaderMediaId('');
    setUploadedMediaName('');
    setTestButtonValues({});
    // Prefill each mapped variable with a readable sample. The dialog used to claim variables
    // were "sent as sample values" while actually sending empty strings, which WhatsApp can
    // reject outright — so a test send could fail for a reason the template itself didn't have.
    setTestVariables(
      Object.fromEntries(
        Object.entries(template?.variableMapping || {}).map(([key, field]) => [
          field,
          `sample ${CONTACT_FIELDS.find((f) => f.value === field)?.label.toLowerCase() || key}`,
        ])
      )
    );
    setIsTestSendOpen(true);

    if (!template) return;

    // Fetched rather than read off the row: the list response carries the flat columns, which
    // cannot say whether a header's text holds a {{1}} or a button's URL does. The backend derives
    // that from the provider's own component list.
    setLoadingRequirements(true);
    try {
      const detail = await apiFetch<WhatsAppTemplate & { sendRequirements: SendRequirements }>(
        `/workspaces/${workspaceId}/whatsapp-templates/${template.id}`
      );
      setRequirements(detail.sendRequirements);
      // The approved sample is NOT pre-filled any more. It is a Meta CDN link — signed, expiring,
      // and served by the very party we would be asking to fetch it — so sending it back produced
      // error 131053 ("Unable to upload the media used in the message") on a send that had already
      // been reported as accepted. It is shown below as a reference image instead.
    } catch {
      toast.error('Could not load what this template needs — try syncing templates first.');
    } finally {
      setLoadingRequirements(false);
    }
  };

  // Falls back to the row's own `headerType` when the requirements fetch failed. The backend
  // refuses a media-header send with no URL either way, so without this the dialog would offer no
  // field for the very thing it is about to be rejected for.
  const headerFormat = requirements?.header?.format ?? testSendTemplate?.headerType;
  const needsMediaHeader = Boolean(headerFormat && MEDIA_HEADER_FORMATS.includes(headerFormat));
  const dynamicButtons = (requirements?.urlButtons || []).filter((b) => b.hasVariable);

  // What WhatsApp will actually accept for this header. Anything else is refused by the upload
  // endpoint, so narrowing the picker saves a round trip and an error nobody enjoys reading.
  const MEDIA_ACCEPT: Record<string, string> = {
    IMAGE: 'image/jpeg,image/png',
    VIDEO: 'video/mp4,video/3gpp',
    DOCUMENT: 'application/pdf',
  };

  const handleMediaUpload = async (file: File | undefined) => {
    if (!file || !testSendTemplate) return;

    setUploadingMedia(true);
    try {
      const form = new FormData();
      form.append('file', file);

      const result = await apiFetch<{ mediaId: string; mimeType: string; size: number }>(
        `/workspaces/${workspaceId}/whatsapp-templates/${testSendTemplate.id}/media`,
        { method: 'POST', body: form }
      );

      setTestHeaderMediaId(result.mediaId);
      setUploadedMediaName(file.name);
      // A file and a URL are alternatives; keeping both would only raise the question of which won.
      setTestHeaderMediaUrl('');
      toast.success(`${file.name} uploaded`);
    } catch (error) {
      // The message is the vendor's or our own validation's — either way it names the actual
      // problem with this file, which is the thing worth showing.
      toast.error(error instanceof Error ? error.message : 'Could not upload that file');
    } finally {
      setUploadingMedia(false);
    }
  };

  // Poll for the real outcome after a send. The send response can only say the provider took the
  // request; whether it ARRIVED comes back later on the status webhook, and this is where that
  // answer surfaces. Gives up after 90s so a dialog left open does not poll forever.
  useEffect(() => {
    const messageId = testResult?.providerMessageId;
    if (!isTestSendOpen || !messageId) return;

    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const status = await apiFetch<TestSendDelivery>(
          `/workspaces/${workspaceId}/whatsapp-templates/test-sends/${encodeURIComponent(messageId)}`
        );
        if (cancelled) return;
        setDelivery(status);
        if (['delivered', 'read', 'failed'].includes(status.status)) clearInterval(timer);
      } catch {
        // A 404 means the row is not written yet, or this deployment never receives the webhook.
        // Neither is worth a toast on a timer.
      }
      if (Date.now() - startedAt > 90_000) clearInterval(timer);
    };

    const timer = setInterval(poll, 3000);
    poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [testResult?.providerMessageId, isTestSendOpen, workspaceId]);

  const handleTestSend = async () => {
    // Each click is a charged send, so a second one while the first is in flight must not fire.
    if (!testPhone || testSending) return;
    setTestSending(true);
    setTestResult(null);
    setDelivery(null);
    try {
      const endpoint = testSendTemplate
        ? `/workspaces/${workspaceId}/whatsapp-templates/${testSendTemplate.id}/test-send`
        : `/workspaces/${workspaceId}/whatsapp-templates/test-send`;

      const result = await apiFetch<TestSendResult>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          phone: testPhone,
          channel: testChannel,
          message: testMessage || undefined,
          // `variables` is keyed by contact field name — testSend passes this object straight
          // to the body builder, which looks up mapping[n] against it.
          variables: testSendTemplate ? testVariables : undefined,
          headerText: requirements?.header?.hasVariable ? testHeaderText : undefined,
          // An uploaded id is preferred; the URL is the fallback for media already hosted somewhere
          // public, and the backend uploads that too rather than passing the link on to WhatsApp.
          headerMediaId: needsMediaHeader && testHeaderMediaId ? testHeaderMediaId : undefined,
          headerMediaUrl: needsMediaHeader && !testHeaderMediaId ? testHeaderMediaUrl : undefined,
          buttonValues: dynamicButtons.length > 0 ? testButtonValues : undefined,
        }),
      });

      // A provider refusal arrives as a successful response carrying the reason — that is the
      // point of this endpoint. The dialog stays open so the diagnostic can be read.
      if (result.accepted === false) {
        setTestResult(result);
        toast.error(result.providerError?.details || result.error || 'The provider refused the message');
        return;
      }

      setTestResult(result);
      if (result.warning) toast.warning(result.warning);
      toast.success(result.creditsCharged
        ? `Test message sent to ${testPhone} — ${formatCredits(result.creditsCharged)} credits`
        : `Test message sent to ${testPhone}`);
    } catch (err) {
      // Our own refusal — an unusable number, a media URL that cannot be fetched, a carousel, too
      // few credits, the hourly cap. Nothing was sent and nothing was charged.
      toast.error(err instanceof Error ? err.message : 'Failed to send test message');
    } finally {
      setTestSending(false);
      refreshWorkspace();
    }
  };

  // Extract {{N}} variables from body text, ordered NUMERICALLY.
  // A default sort is lexicographic, which puts {{10}} before {{2}} — wrong once a template
  // has more than nine variables, and the order here drives the mapping rows below.
  // Shaped as `{{1}}` rather than `1` because that is what the rows below render. The extraction
  // and the numeric ordering come from `@/lib/templateVariables`, which the launch dialog shares —
  // that page carried its own copy with a broken regex for its whole life, so there is now one.
  // Named variables ({{name}}, from templates authored in the provider console) are included when
  // the body has no numbered ones, matching what the send fills.
  const extractVariables = (body: string): string[] =>
    extractTemplateVariables(body).map((index) => `{{${index}}}`);

  // The variable inserter and the buttons builder that used to live here belonged to the create
  // form and went with it. Nothing is lost: body text, variables and buttons are all authored by
  // Targetna at the provider, arrive through the sync, and are shown in the detail dialog.
  // `extractVariables` above stays — it drives the mapping editor and the detail view.

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <CardTitle>WhatsApp Templates</CardTitle>
                <Badge variant="outline" className="ml-2">
                  {total} total
                </Badge>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* The quota is server-reported: the client knows the limit but not the used count.
                    A null limit means unlimited, which is also what an admin sees. */}
                {capabilities && capabilities.templatesLimit !== null && canManage && (
                  <span className="text-xs text-muted-foreground">
                    {capabilities.templatesUsed} of {capabilities.templatesLimit} templates used
                  </span>
                )}
                {/* Hidden without templates:manage — a media or viewer role can never do this and a
                    disabled button is noise. Hidden too when the server cannot create at all, which
                    is a misconfiguration rather than an upsell. Shown WITHOUT the plan feature on
                    purpose: the click produces a 403 that useApiErrorToast renders as "View plans",
                    and hiding a paid feature sells nothing. */}
                {canManage && capabilities?.canCreate && (
                  <Button
                    size="sm"
                    disabled={atQuota}
                    title={atQuota ? `Your plan allows ${capabilities.templatesLimit} templates.` : undefined}
                    onClick={guard(() => openComposer())}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Template
                  </Button>
                )}
                {/* Free-text, not a template — a plain SMS or WhatsApp message typed by hand.
                    Named apart from the per-template "Test Send" in each row's menu, which is the
                    one that exercises an approved template. */}
                <Button variant="outline" size="sm" onClick={guard(() => openTestSend())}>
                  <Send className="mr-2 h-4 w-4" />
                  Test Free Text
                </Button>
                <Button variant="outline" size="sm" onClick={guard(() => syncTemplates())} disabled={syncing}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                  Sync templates
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {syncIssue && (
              <div
                className={`mb-6 flex gap-3 rounded-lg border p-4 ${
                  syncIssue.kind === 'forbidden' || syncIssue.kind === 'misconfigured'
                    ? 'border-amber-300 bg-amber-50 text-amber-900'
                    : 'border-muted bg-muted/50 text-muted-foreground'
                }`}
              >
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p className="font-medium">Templates are not syncing</p>
                  <p>{syncIssue.message}</p>
                  {syncIssue.kind === 'forbidden' && (
                    <p className="text-xs">
                      Any templates below were synced earlier and are still usable. Variable
                      mappings can still be edited.
                    </p>
                  )}
                </div>
              </div>
            )}

            {reviewCount > 0 && (
              <div className="mb-6 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p className="font-medium">
                    {reviewCount} template{reviewCount === 1 ? '' : 's'} changed at the provider and
                    need{reviewCount === 1 ? 's' : ''} the variable mapping confirmed
                  </p>
                  <p>
                    A variable mapping is positional — {'{{2}}'} means "whichever blank is second in
                    this wording". When the wording changes, {'{{2}}'} can come to mean something
                    else, and a campaign would then deliver the wrong personal detail to every
                    recipient. Open <span className="font-medium">Edit Variable Mapping</span> on the
                    flagged rows below, check the mapping still reads correctly, and save to clear
                    this. Campaigns using them cannot launch until you do.
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-4 mb-6">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                  <SelectItem value="PAUSED">Paused</SelectItem>
                  <SelectItem value="DISABLED">Disabled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="MARKETING">Marketing</SelectItem>
                  <SelectItem value="UTILITY">Utility</SelectItem>
                  <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No templates yet</p>
                {/* Only offers creation when it would actually work. Without that, this is the same
                    bare message it has always been — it deliberately does not explain how to get a
                    template, because that would mean naming the provider console to a customer. */}
                {canManage && capabilities?.canCreate && (
                  <>
                    <p className="mt-2 text-sm">
                      WhatsApp campaigns need an approved message template before they can be sent.
                    </p>
                    <Button className="mt-4" size="sm" disabled={atQuota} onClick={guard(() => openComposer())}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create your first template
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Body</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell className="font-medium">
                          {formatTemplateName(template.name)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{template.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[template.status] || ''}>
                            {template.status}
                          </Badge>
                          {template.status === 'REJECTED' && template.rejectionReason && (
                            <p className="text-xs text-red-600 mt-1">{template.rejectionReason}</p>
                          )}
                          {/* Sits under the status rather than replacing it: the template is still
                              APPROVED at the provider — it is our mapping that is unconfirmed. */}
                          {needsMappingReview(template) && (
                            <Badge
                              variant="outline"
                              className="mt-1 border-amber-300 bg-amber-50 text-amber-900"
                            >
                              Needs review
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{template.language}</TableCell>
                        <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">
                          {template.body}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(template.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openDetails(template)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {/* The hand-off to a campaign. Approved only: the wizard lists nothing
                                  else, and the server refuses to start one from anything else. */}
                              {template.status === 'APPROVED' && canCreateCampaign && (
                                <DropdownMenuItem
                                  onClick={guard(() => navigate(campaignFromTemplatePath(workspaceId, template.id)))}
                                >
                                  <Megaphone className="mr-2 h-4 w-4" />
                                  Create campaign
                                </DropdownMenuItem>
                              )}
                              {template.status === 'APPROVED' && (
                                <DropdownMenuItem onClick={guard(() => openTestSend(template))}>
                                  <Send className="mr-2 h-4 w-4" />
                                  Test Send
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={guard(() => openMappingEditor(template))}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit Variable Mapping
                              </DropdownMenuItem>
                              {/* Only on templates this workspace authored. Mirrors the server's
                                  templateWriteRefusal — offering an action that would be refused is
                                  worse than not offering it. PENDING is excluded because Meta
                                  permits no edits while a template is under review. */}
                              {canManage && canEditTemplate(template) && template.status !== 'PENDING' && (
                                <DropdownMenuItem onClick={guard(() => openComposer(template))}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit Template
                                </DropdownMenuItem>
                              )}
                              {canManage && canDeleteTemplate(template) && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={guard(() => setDeleteTarget(template))}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Template
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Template Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Template Details</DialogTitle>
            </DialogHeader>
            {selectedTemplate && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[selectedTemplate.status] || ''}>
                    {selectedTemplate.status}
                  </Badge>
                  <Badge variant="outline">{selectedTemplate.category}</Badge>
                  {selectedTemplate.visibility === 'public' && (
                    <Badge variant="outline" className="gap-1">
                      <Globe className="h-3 w-3" />
                      Shared
                    </Badge>
                  )}
                  {selectedTemplate.metadata?.createdVia === 'meta' && (
                    <Badge variant="outline">Created here</Badge>
                  )}
                  <span className="text-sm text-muted-foreground ml-auto">{selectedTemplate.language}</span>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="text-sm">{formatTemplateName(selectedTemplate.name)}</p>
                </div>

                {selectedTemplate.headerType !== 'NONE' && selectedTemplate.headerContent && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Header ({selectedTemplate.headerType})</Label>
                    <p className="text-sm">{selectedTemplate.headerContent}</p>
                  </div>
                )}

                {/* What a campaign using this template starts with. Ours alone, so it changes
                    here without sending the template back to WhatsApp for review. */}
                {selectedTemplate.campaignMediaId && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Default campaign media</Label>
                    {detailMedia === undefined ? (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                      </p>
                    ) : detailMedia === null ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        The media this template was made from is no longer available, so campaigns
                        will ask for one.
                      </p>
                    ) : (
                      <div className="mt-1 flex items-start gap-3">
                        {detailMedia.previewUrl && detailMedia.kind === 'photo' && (
                          <img src={detailMedia.previewUrl} alt="" className="h-16 w-16 shrink-0 rounded border object-cover" />
                        )}
                        {detailMedia.previewUrl && detailMedia.kind === 'video' && (
                          <video
                            src={detailMedia.previewUrl}
                            className="h-16 w-16 shrink-0 rounded border bg-black object-cover"
                            muted
                            playsInline
                            preload="metadata"
                          />
                        )}
                        <div className="min-w-0 text-sm">
                          <p className="truncate" dir="auto">{detailMedia.mediaName}</p>
                          <p className="text-xs text-muted-foreground">
                            Campaigns using this template start with this media
                            {detailMedia.assetUid ? ' and its header' : ''}. Each campaign can still
                            choose another.
                          </p>
                        </div>
                      </div>
                    )}
                    {canManage && detailMedia && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {ASSET_KIND_FOR_HEADER[selectedTemplate.headerType] && detailAssets === null && (
                          <Button size="sm" variant="outline" disabled={savingDefault} onClick={guard(loadDetailAssets)}>
                            Change {selectedTemplate.headerType.toLowerCase()}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={savingDefault}
                          onClick={guard(() => saveDefaultMedia({ campaignMediaId: null }))}
                        >
                          Remove default
                        </Button>
                      </div>
                    )}
                    {detailAssets && detailAssets.length > 0 && (
                      <div className="mt-2">
                        <MediaAssetGrid
                          assets={detailAssets}
                          chosenUid={selectedTemplate.headerAssetUid ?? null}
                          busy={savingDefault}
                          disabled={savingDefault}
                          label="Default header"
                          onPick={(asset) => guard(() => saveDefaultMedia({ headerAssetUid: asset.uid }))()}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-xs text-muted-foreground">Body</Label>
                  <p className="text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-3">{selectedTemplate.body}</p>
                </div>

                {needsMappingReview(selectedTemplate) && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Previously read (until{' '}
                      {new Date(selectedTemplate.metadata!.mappingReview!.since).toLocaleDateString()})
                    </Label>
                    <p className="text-sm whitespace-pre-wrap bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
                      {selectedTemplate.metadata!.mappingReview!.previousBody}
                    </p>
                  </div>
                )}

                {selectedTemplate.footer && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Footer</Label>
                    <p className="text-sm text-muted-foreground">{selectedTemplate.footer}</p>
                  </div>
                )}

                {selectedTemplate.buttons.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Buttons</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedTemplate.buttons.map((btn, i) => (
                        <Badge key={i} variant="secondary">
                          {btn.text} ({btn.type})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(selectedTemplate.variableMapping).length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Variable Mapping</Label>
                    <div className="space-y-1 mt-1">
                      {Object.entries(selectedTemplate.variableMapping).map(([key, field]) => (
                        <div key={key} className="flex items-center gap-2 text-sm">
                          <span className="font-mono">{`{{${key}}}`}</span>
                          <span className="text-muted-foreground">&rarr;</span>
                          <span>{field}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTemplate.status === 'REJECTED' && selectedTemplate.rejectionReason && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <Label className="text-xs text-red-700">Rejection Reason</Label>
                    <p className="text-sm text-red-800">{selectedTemplate.rejectionReason}</p>
                  </div>
                )}

                <div className="text-xs text-muted-foreground space-y-1">
                  {selectedTemplate.submittedAt && (
                    <p>Submitted: {new Date(selectedTemplate.submittedAt).toLocaleString()}</p>
                  )}
                  {selectedTemplate.approvedAt && (
                    <p>Approved: {new Date(selectedTemplate.approvedAt).toLocaleString()}</p>
                  )}
                  <p>Created: {new Date(selectedTemplate.createdAt).toLocaleString()}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Variable Mapping Editor */}
        <Dialog open={isMappingOpen} onOpenChange={setIsMappingOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Variable Mapping: {formatTemplateName(selectedTemplate?.name)}</DialogTitle>
            </DialogHeader>
            {selectedTemplate && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Each variable is mapped to a contact field automatically where the template's
                  example makes it unambiguous. Anything left blank needs choosing here. This is
                  stored only in Targetna — it is not part of the approved template itself, so you
                  can change it at any time and the template stays approved.
                </p>

                {needsMappingReview(selectedTemplate) && (() => {
                  const review = selectedTemplate.metadata!.mappingReview!;
                  const labels = selectedTemplate.metadata?.variableLabels || {};
                  const describe = review.changedIndices
                    .map((i) => (labels[i] ? `{{${i}}} (${labels[i]})` : `{{${i}}}`))
                    .join(', ');
                  return (
                    <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                      <div className="text-sm space-y-2">
                        <p className="font-medium">
                          This template was changed at the provider on{' '}
                          {new Date(review.since).toLocaleDateString()}
                        </p>
                        <p>
                          {describe
                            ? `${describe} may no longer mean what ${review.changedIndices.length === 1
                              ? 'it did' : 'they did'} when the mapping was saved.`
                            : 'The wording changed, and the mapping below was set against the old '
                              + 'wording.'}
                          {' Check each row still reads correctly against the new text, then save. '}
                          Saving confirms the mapping and clears this — campaigns using this template
                          cannot launch until you do.
                        </p>
                        <div>
                          <p className="text-xs font-medium mb-1">Previously read:</p>
                          <p className="text-xs whitespace-pre-wrap font-mono opacity-70">
                            {review.previousBody}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="rounded-lg bg-muted/50 p-3">
                  {needsMappingReview(selectedTemplate) && (
                    <p className="text-xs font-medium text-muted-foreground mb-1">Now reads:</p>
                  )}
                  <p className="text-sm whitespace-pre-wrap font-mono">{selectedTemplate.body}</p>
                </div>

                {extractVariables(selectedTemplate.body).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    This template has no variables.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {extractVariables(selectedTemplate.body).map((v) => {
                      const key = v.replace(/[{}]/g, '');
                      const label = selectedTemplate.metadata?.variableLabels?.[key];
                      return (
                        <div key={v} className="flex items-center gap-3">
                          <div className="w-[150px] shrink-0">
                            <span className="font-mono text-sm">{v}</span>
                            {label && (
                              // What the template's author typed as the example for this variable.
                              // It is the only clue to what belongs here, so it is worth showing
                              // even — especially — when it was too ambiguous to map automatically.
                              <span className="block text-xs text-muted-foreground truncate" title={label}>
                                {label}
                              </span>
                            )}
                          </div>
                          <Select
                            value={mappingDraft[key] || ''}
                            onValueChange={(val) =>
                              setMappingDraft((prev) => ({ ...prev, [key]: val }))
                            }
                          >
                            <SelectTrigger className="w-[220px]">
                              <SelectValue placeholder="Select field..." />
                            </SelectTrigger>
                            <SelectContent>
                              {CONTACT_FIELDS.map((field) => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!mappingDraft[key] && (
                            <span className="text-xs text-amber-600 whitespace-nowrap">Not mapped</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {extractVariables(selectedTemplate.body).some(
                  (v) => !mappingDraft[v.replace(/[{}]/g, '')]
                ) && (
                  // Not a nag. An unmapped variable means we send fewer parameters than the
                  // approved template declares, WhatsApp rejects every message, and the campaign
                  // fails in full — so launching is blocked until this is finished.
                  <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-xs">
                      Campaigns using this template cannot be launched until every variable is
                      mapped — WhatsApp rejects a message whose variables are not all filled in.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setIsMappingOpen(false)}>
                    Cancel
                  </Button>
                  {/* Relabelled under review because that is what the click means there: the
                      mapping may not need editing at all, and saving it unchanged is the normal
                      way to record "I looked, it still reads correctly". The server clears the
                      flag on any mapping save, so no edit is required to resolve one. */}
                  <Button onClick={handleSaveMapping} disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {needsMappingReview(selectedTemplate) ? 'Confirm Mapping' : 'Save Mapping'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Test Send Dialog */}
        <Dialog open={isTestSendOpen} onOpenChange={setIsTestSendOpen}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle>
                {testSendTemplate ? `Test Send: ${formatTemplateName(testSendTemplate.name)}` : 'Send Free Text Test'}
              </DialogTitle>
              <DialogDescription>
                {isSuperAdmin
                  ? 'Test sends are free for platform admins.'
                  : 'A test send is a real message, charged at your plan’s normal message rate. If the provider refuses it, the credits come back. Up to 30 test sends per hour.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test-phone">Phone Number</Label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <Input
                    id="test-phone"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+971501234567"
                  />
                </div>
              </div>

              {!testSendTemplate && (
                <>
                  <div className="space-y-2">
                    <Label>Channel</Label>
                    <Select value={testChannel} onValueChange={(v) => setTestChannel(v as 'sms' | 'whatsapp')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="test-message">Message</Label>
                    <Textarea
                      id="test-message"
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Enter test message..."
                      rows={3}
                    />
                  </div>
                </>
              )}

              {testSendTemplate && (
                <>
                  <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                    <Label className="text-xs text-muted-foreground">Template Preview</Label>
                    <p className="text-sm whitespace-pre-wrap font-mono">{testSendTemplate.body}</p>
                    {testSendTemplate.footer && (
                      <p className="text-xs text-muted-foreground">{testSendTemplate.footer}</p>
                    )}
                  </div>

                  {/* Warned, never blocked. A test send to your own number is exactly how you check
                      whether a mapping still reads correctly after the provider changed the body,
                      so gating it behind the confirmation would remove the only way to do that. */}
                  {needsMappingReview(testSendTemplate) && (
                    <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <p className="text-xs">
                        This template was changed at the provider and its mapping is not confirmed
                        yet. A test send is a good way to check it — look at whether each value
                        lands where you expect, then confirm the mapping to unblock campaigns.
                      </p>
                    </div>
                  )}

                  {loadingRequirements && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking what this template needs...
                    </div>
                  )}

                  {requirements?.hasCarousel && (
                    <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <p className="text-xs">
                        This is a carousel template. Carousels are not supported yet, so a test send
                        will be refused.
                      </p>
                    </div>
                  )}

                  {needsMediaHeader && (
                    <div className="space-y-2">
                      <Label htmlFor="test-header-media-file" className="text-xs text-muted-foreground">
                        Header {headerFormat!.toLowerCase()}
                      </Label>

                      <Input
                        id="test-header-media-file"
                        type="file"
                        accept={MEDIA_ACCEPT[headerFormat!] || undefined}
                        disabled={uploadingMedia}
                        onChange={(e) => handleMediaUpload(e.target.files?.[0])}
                      />

                      {uploadingMedia && (
                        <p className="text-xs text-muted-foreground">Uploading…</p>
                      )}

                      {testHeaderMediaId && !uploadingMedia && (
                        <p className="text-xs text-green-700">
                          {uploadedMediaName} uploaded — it will be sent by reference, so WhatsApp
                          does not have to fetch it.
                        </p>
                      )}

                      {!testHeaderMediaId && (
                        <>
                          <p className="text-xs text-muted-foreground">
                            Or paste a public HTTPS URL. We download it and upload it for you, so it
                            only has to be reachable right now, not when WhatsApp gets around to it.
                          </p>
                          <Input
                            id="test-header-media"
                            value={testHeaderMediaUrl}
                            onChange={(e) => setTestHeaderMediaUrl(e.target.value)}
                            placeholder="https://..."
                          />
                        </>
                      )}

                      {requirements?.header?.sampleMediaUrl && (
                        <p className="text-xs text-muted-foreground">
                          This template was approved with{' '}
                          <a
                            href={requirements.header.sampleMediaUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            this sample
                          </a>
                          . It is a WhatsApp CDN link that expires and cannot be sent back to them —
                          upload the file itself instead.
                        </p>
                      )}
                    </div>
                  )}

                  {requirements?.header?.hasVariable && (
                    <div className="space-y-2">
                      <Label htmlFor="test-header-text" className="text-xs text-muted-foreground">
                        Header value
                      </Label>
                      {requirements.header.text && (
                        <p className="text-xs font-mono text-muted-foreground">{requirements.header.text}</p>
                      )}
                      <Input
                        id="test-header-text"
                        value={testHeaderText}
                        onChange={(e) => setTestHeaderText(e.target.value)}
                        placeholder="Value for the header variable"
                      />
                    </div>
                  )}

                  {Object.keys(testSendTemplate.variableMapping || {}).length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Test values</Label>
                      {Object.entries(testSendTemplate.variableMapping).map(([key, field]) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className="font-mono text-xs w-12">{`{{${key}}}`}</span>
                          <Input
                            value={testVariables[field] ?? ''}
                            onChange={(e) =>
                              setTestVariables((prev) => ({ ...prev, [field]: e.target.value }))
                            }
                            placeholder={field}
                          />
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground">
                        Empty values may be rejected by WhatsApp.
                      </p>
                    </div>
                  )}

                  {dynamicButtons.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Button link values</Label>
                      {dynamicButtons.map((button) => (
                        <div key={button.index} className="space-y-1">
                          <p className="text-xs text-muted-foreground truncate" title={button.url || ''}>
                            {button.text || `Button ${button.index + 1}`} — {button.url}
                          </p>
                          <Input
                            value={testButtonValues[String(button.index)] ?? ''}
                            onChange={(e) =>
                              setTestButtonValues((prev) => ({ ...prev, [String(button.index)]: e.target.value }))
                            }
                            placeholder="Value for the link variable"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* The diagnostic. A provider refusal is shown in full rather than summarised —
                  the reason it was refused and the exact components sent are the two things that
                  make the next attempt better. */}
              {testResult && (
                <div
                  className={`rounded-lg border p-3 text-sm space-y-2 ${
                    testResult.accepted
                      ? 'border-green-300 bg-green-50 text-green-900'
                      : 'border-red-300 bg-red-50 text-red-900'
                  }`}
                >
                  <p className="font-medium">
                    {testResult.accepted ? 'Accepted by the provider' : 'Refused by the provider'}
                  </p>

                  {testResult.accepted ? (
                    <div className="space-y-1">
                      <p className="text-xs font-mono break-all">
                        {testResult.providerMessageId || 'no message id returned'}
                      </p>

                      {/* "Accepted" has never meant "delivered" here — that was the whole 131053
                          problem. These two lines say which path ran, so a message that never
                          arrives can be explained without reading server logs. */}
                      {testResult.mediaMode === 'id' && (
                        <p className="text-xs">
                          Image sent by reference — WhatsApp had nothing to fetch.
                        </p>
                      )}
                      {testResult.mediaMode === 'link' && (
                        <p className="text-xs font-medium text-red-700">
                          Sent as a link — WhatsApp still has to fetch it, which is the path that
                          fails silently.
                        </p>
                      )}

                      {testResult.recipientResolved === false && (
                        <p className="text-xs font-medium text-red-700">
                          This number did not resolve to a WhatsApp account, so it will probably
                          never arrive. Check the country code and that the number uses WhatsApp.
                        </p>
                      )}

                      {/* The real answer, once WhatsApp reports back. Until then this says it is
                          waiting rather than claiming success — the previous version called the
                          provider's 200 "delivered", which is what made a vanished message look
                          like a delivered one. */}
                      <div className="rounded border border-current/20 bg-white/40 px-2 py-1.5">
                        <p className="text-xs font-medium">
                          {delivery ? DELIVERY_LABELS[delivery.status] : DELIVERY_LABELS.accepted}
                        </p>

                        {delivery?.status === 'failed' && (
                          <p className="mt-1 text-xs text-red-700">
                            {delivery.errorMessage || 'WhatsApp gave no reason.'}
                            {delivery.failureCode ? ` (code ${delivery.failureCode})` : ''}
                          </p>
                        )}

                        {(!delivery || delivery.status === 'accepted') && (
                          <p className="mt-1 text-xs opacity-70">
                            If this never changes, WhatsApp status events are not reaching this
                            environment — check the Message Status Webhook in the CEQUENS console.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs space-y-1">
                      {testResult.providerError?.details && <p>{testResult.providerError.details}</p>}
                      {testResult.providerError?.title && !testResult.providerError?.details && (
                        <p>{testResult.providerError.title}</p>
                      )}
                      {!testResult.providerError && testResult.error && <p>{testResult.error}</p>}
                      <p className="opacity-70">
                        {[
                          testResult.providerStatus ? `HTTP ${testResult.providerStatus}` : null,
                          testResult.providerError?.code ? `code ${testResult.providerError.code}` : null,
                          testResult.requestId ? `request ${testResult.requestId}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  )}

                  {testResult.sentComponents !== undefined && (
                    <details className="text-xs">
                      <summary className="cursor-pointer opacity-80">What we sent</summary>
                      <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-black/5 p-2">
                        {JSON.stringify(testResult.sentComponents, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsTestSendOpen(false)}>
                  {testResult ? 'Close' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleTestSend}
                  disabled={testSending || !testPhone || loadingRequirements || requirements?.hasCarousel}
                >
                  {testSending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="mr-2 h-4 w-4" /> {testResult ? 'Send Again' : 'Send Test'}</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {composerOpen && (
          <TemplateComposerDialog
            open={composerOpen}
            onOpenChange={setComposerOpen}
            mode={composerMode}
            initial={composerInitial}
            canUploadSamples={Boolean(capabilities?.canUploadSamples)}
            submitting={composerSubmitting}
            onUploadSample={uploadSample}
            onSubmit={submitComposer}
            mediaAssets={composerAssets}
            sourceMediaName={composerMedia?.name ?? null}
            onUseAsset={composerMedia ? prepareAssetSample : undefined}
          />
        )}

        <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete &ldquo;{deleteTarget ? formatTemplateName(deleteTarget.name) : ''}&rdquo;?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {/* The name lock is stated BEFORE the decision. Discovering it afterwards, when the
                    replacement is refused, is far more annoying than being told now. */}
                This removes the template from WhatsApp. Campaigns already sent are unaffected. A
                template used by a scheduled or running campaign can&rsquo;t be deleted until that
                campaign ends. WhatsApp also reserves the name for 30 days, so it cannot be reused
                immediately.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {nudge}
      </div>
    </div>
  );
}
